// Importar el catálogo desde una planilla (Excel o CSV): todo lo que no toca la pantalla ni Firestore,
// para poder probarlo solo. El flujo es: filas crudas -> columnas -> productos revisados -> plan de cambios.
import { cleanLine, cleanParagraph, textProblem } from "./businessValidation.js";
import { cleanTag, MAX_TAGS, PRODUCT_TAGS } from "./menu.js";

export const IMPORT_MAX_ROWS = 1000;
export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const MAX_CATEGORIES = 100;

// Campos de MiQRapp a los que puede ir cada columna del archivo.
export const FIELDS = [
    { id: "name", label: "Nombre", required: true },
    { id: "price", label: "Precio", required: true },
    { id: "category", label: "Categoría", required: true },
    { id: "description", label: "Descripción" },
    { id: "etiquetas", label: "Etiquetas" },
    { id: "agotado", label: "Agotado (SÍ = agotado)" },
    { id: "disponible", label: "Disponible / Stock (NO = agotado)" }
];
export const SKIP = "";

// Encabezados de la plantilla, tal cual.
export const TEMPLATE_HEADERS = ["Nombre", "Precio", "Categoría", "Descripción", "Etiquetas", "Agotado"];

// Para comparar textos sin importar mayúsculas, tildes ni espacios de más.
export const norm = (value) => String(value ?? "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/\s+/g, " ").trim();

// Nombres de columna que se reconocen solos. El primero de cada lista es el de la plantilla.
const SYNONYMS = {
    name: ["nombre", "producto", "productos", "nombre del producto", "articulo", "item", "plato", "descripcion corta", "name", "product"],
    price: ["precio", "precio venta", "precio de venta", "precio final", "pvp", "valor", "importe", "precio unitario", "price", "$"],
    category: ["categoria", "rubro", "seccion", "tipo", "familia", "grupo", "category"],
    description: ["descripcion", "detalle", "detalles", "ingredientes", "descripcion larga", "info", "description"],
    etiquetas: ["etiquetas", "etiqueta", "tags", "tag", "destacado"],
    agotado: ["agotado", "agotados", "sin stock", "sold out"],
    disponible: ["disponible", "stock", "en stock", "hay stock", "activo", "disponibilidad", "available"]
};

// Lee un CSV (separado por coma, punto y coma o tabulación) y devuelve las filas como listas de textos.
export const parseCsv = (text) => {
    const clean = text.replace(/^﻿/, "");
    const firstLine = clean.split(/\r?\n/, 1)[0] || "";
    const counts = [";", ",", "\t"].map((d) => [d, firstLine.split(d).length]);
    const sep = counts.sort((a, b) => b[1] - a[1])[0][0];

    const rows = [];
    let row = [];
    let cell = "";
    let quoted = false;
    for (let i = 0; i < clean.length; i++) {
        const ch = clean[i];
        if (quoted) {
            if (ch === '"') {
                if (clean[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
            } else cell += ch;
        } else if (ch === '"' && cell === "") {
            quoted = true;
        } else if (ch === sep) {
            row.push(cell); cell = "";
        } else if (ch === "\n" || ch === "\r") {
            if (ch === "\r" && clean[i + 1] === "\n") i++;
            row.push(cell); rows.push(row); row = []; cell = "";
        } else cell += ch;
    }
    if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
    return rows;
}

// Los CSV de Excel en castellano suelen venir en Windows-1252: si UTF-8 deja caracteres rotos, se reintenta.
export const decodeText = (buffer) => {
    const utf8 = new TextDecoder("utf-8").decode(buffer);
    if (!utf8.includes("�")) return utf8;
    try {
        return new TextDecoder("windows-1252").decode(buffer);
    } catch (error) {
        return utf8;
    }
}

// Saca filas vacías y separa encabezados de datos. Busca la fila de encabezados entre las primeras 10:
// la primera que tenga al menos dos celdas con texto.
export const splitTable = (rows) => {
    const filled = rows.map((r) => r.map((c) => String(c ?? "").trim()));
    const headerIndex = filled.findIndex((r, i) => i < 10 && r.filter(Boolean).length >= 2);
    if (headerIndex < 0) return { headers: [], rows: [] };
    const width = Math.max(...filled.slice(headerIndex).map((r) => r.length));
    const headers = Array.from({ length: width }, (_, i) => filled[headerIndex][i] || `Columna ${i + 1}`);
    const data = filled.slice(headerIndex + 1)
        .filter((r) => r.some(Boolean))
        .map((r) => Array.from({ length: width }, (_, i) => r[i] || ""));
    return { headers, rows: data };
}

// Para cada columna, el campo de MiQRapp que le corresponde (o SKIP). Cada campo se usa una sola vez.
export const detectMapping = (headers) => {
    const used = new Set();
    return headers.map((h) => {
        const n = norm(h).replace(/[*:]/g, "").trim();
        const field = Object.keys(SYNONYMS).find((f) => !used.has(f) && SYNONYMS[f].includes(n));
        if (field) used.add(field);
        return field || SKIP;
    });
}

// Si el archivo es la plantilla (todas las columnas reconocidas y están las obligatorias), se saltea el paso de columnas.
export const mappingIsComplete = (mapping) => FIELDS.filter((f) => f.required).every((f) => mapping.includes(f.id));
export const looksLikeTemplate = (headers, mapping) => mappingIsComplete(mapping) && mapping.every((m) => m !== SKIP)
    && headers.every((h) => TEMPLATE_HEADERS.some((t) => norm(t) === norm(h).replace(/[*:]/g, "").trim()));

// Huella de los encabezados, para acordarse de cómo se mapearon la próxima vez.
export const headersKey = (headers) => headers.map(norm).join("|");

// "$ 1.500,50" -> "1500.50". Devuelve null si no es un precio válido.
export const parsePrice = (value) => {
    if (typeof value === "number") return Number.isFinite(value) && value >= 0 ? String(Math.round(value * 100) / 100) : null;
    let s = String(value ?? "").replace(/\s|\$|ars/gi, "");
    if (!s) return null;
    const lastDot = s.lastIndexOf(".");
    const lastComma = s.lastIndexOf(",");
    if (lastDot >= 0 && lastComma >= 0) {
        // Con los dos, el último es el decimal.
        const dec = lastDot > lastComma ? "." : ",";
        const thousands = dec === "." ? "," : ".";
        s = s.split(thousands).join("").replace(dec, ".");
    } else if (lastComma >= 0 || lastDot >= 0) {
        const sep = lastComma >= 0 ? "," : ".";
        const parts = s.split(sep);
        // 1.500 o 12.500.000 son miles; 1500,5 o 9.99 son decimales.
        const isThousands = parts.length > 2 || (parts[1].length === 3 && parts[0].length >= 1 && parts[0] !== "0");
        s = isThousands ? parts.join("") : parts.join(".");
    }
    if (!/^[0-9]{1,9}(\.[0-9]+)?$/.test(s)) return null;
    const n = Math.round(Number(s) * 100) / 100;
    if (!Number.isFinite(n) || n >= 100000000) return null;
    return String(n);
}

const YES = ["si", "s", "x", "1", "true", "verdadero", "yes", "y", "agotado", "sin stock", "no hay"];
const NO = ["no", "n", "0", "false", "falso", "", "disponible", "hay", "en stock", "ok"];

// Sí / No de la planilla. Devuelve null si no se entiende.
export const parseYesNo = (value) => {
    const n = norm(value);
    if (YES.includes(n)) return true;
    if (NO.includes(n)) return false;
    if (/^[0-9]+$/.test(n)) return Number(n) > 0;
    return null;
}

// "nuevo, sin tacc" -> ["Nuevo", "Sin TACC"]: usa la forma sugerida si coincide.
export const parseTags = (value) => {
    const tags = [];
    String(value ?? "").split(/[,;|/]/).map(cleanTag).filter(Boolean).forEach((t) => {
        const tag = PRODUCT_TAGS.find((p) => norm(p) === norm(t)) || t;
        if (!tags.some((x) => norm(x) === norm(tag))) tags.push(tag);
    });
    return tags.slice(0, MAX_TAGS);
}

// Arma un producto por fila, con los textos ya limpios. `raw` guarda lo que vino, para editarlo.
export const buildRows = (rows, mapping) => rows.map((cells, index) => {
    const get = (field) => {
        const i = mapping.indexOf(field);
        return i >= 0 ? cells[i] : undefined;
    };
    const row = {
        key: index,
        line: index + 1,
        name: cleanLine(get("name")),
        price: String(get("price") ?? "").trim(),
        category: cleanLine(get("category")),
        description: cleanParagraph(get("description") ?? ""),
        skipped: false
    };
    if (mapping.includes("etiquetas")) row.etiquetas = parseTags(get("etiquetas"));
    if (mapping.includes("agotado")) row.agotado = parseYesNo(get("agotado"));
    else if (mapping.includes("disponible")) {
        const available = parseYesNo(get("disponible"));
        row.agotado = available === null ? null : !available;
    }
    return row;
});

// Productos que ya existen, por nombre (sin importar mayúsculas ni tildes).
export const indexByName = (products) => {
    const map = new Map();
    products.forEach((p) => {
        const key = norm(p.name);
        if (key && !map.has(key)) map.set(key, p);
    });
    return map;
}

// Revisa cada fila y le agrega `errors` (bloquean) y `existing` (el producto que ya está cargado con ese nombre).
export const checkRows = (rows, products, categories) => {
    const byName = indexByName(products);
    const seen = new Map();
    rows.forEach((r) => {
        if (r.skipped) return;
        const k = norm(r.name);
        seen.set(k, (seen.get(k) || 0) + 1);
    });
    const known = new Set(categories.map(norm));

    return rows.map((r) => {
        const errors = {};
        const nameProblem = textProblem("El nombre", r.name, 80);
        if (nameProblem) errors.name = r.name ? nameProblem.replace("El nombre", "Nombre") : "Falta el nombre";
        else if (seen.get(norm(r.name)) > 1) errors.name = "Está repetido en el archivo";

        if (!r.price) errors.price = "Falta el precio";
        else if (parsePrice(r.price) === null) errors.price = "El precio no es un número";

        const categoryProblem = textProblem("La categoría", r.category, 40);
        if (categoryProblem) errors.category = r.category ? categoryProblem.replace("La categoría", "Categoría") : "Falta la categoría";

        if (r.description) {
            const p = textProblem("La descripción", r.description, 400);
            if (p) errors.description = p.replace("La descripción", "Descripción");
        }

        return {
            ...r,
            errors,
            hasErrors: Object.keys(errors).length > 0,
            existing: byName.get(norm(r.name)) || null,
            newCategory: !!r.category && !known.has(norm(r.category))
        };
    });
}

// Categorías nuevas que trae el archivo, con la forma en que vienen escritas y en el orden en que aparecen.
export const newCategories = (rows, categories) => {
    const known = new Set(categories.map(norm));
    const found = [];
    rows.forEach((r) => {
        if (r.skipped || !r.category) return;
        const k = norm(r.category);
        if (!known.has(k) && !found.some((c) => norm(c) === k)) found.push(r.category);
    });
    return found;
}

// Plan de cambios. Modo "sumar": crea los nuevos y completa los que ya existen sin tocar foto ni,
// salvo que se pida, precio. Modo "reemplazar": borra todo y crea todo de nuevo.
export const planImport = (rows, products, categories, { mode = "sumar", updatePrices = false, createCategories = true, pro = true } = {}) => {
    const active = rows.filter((r) => !r.skipped && !r.hasErrors);
    const fresh = mode === "reemplazar";
    const added = newCategories(active, fresh ? [] : categories);
    const canonical = new Map([...(fresh ? [] : categories), ...added].map((c) => [norm(c), c]));

    const usable = active.filter((r) => fresh || createCategories || !r.newCategory);
    const skippedByCategory = active.length - usable.length;

    const productFields = (r) => {
        const fields = { name: r.name, price: parsePrice(r.price), category: canonical.get(norm(r.category)) || r.category };
        if (r.description) fields.description = r.description;
        if (pro && r.etiquetas?.length) fields.etiquetas = r.etiquetas;
        if (typeof r.agotado === "boolean") fields.agotado = pro ? r.agotado : false;
        return fields;
    };

    const create = [];
    const update = [];
    usable.forEach((r) => {
        const fields = productFields(r);
        if (!fresh && r.existing) {
            const changes = { category: fields.category };
            if (fields.description) changes.description = fields.description;
            if (fields.etiquetas) changes.etiquetas = fields.etiquetas;
            if (typeof fields.agotado === "boolean") changes.agotado = fields.agotado;
            if (updatePrices) changes.price = fields.price;
            const really = Object.fromEntries(Object.entries(changes).filter(([k, v]) => JSON.stringify(r.existing[k] ?? (k === "agotado" ? false : null)) !== JSON.stringify(v)));
            update.push({ product: r.existing, changes: really });
        } else {
            create.push({ agotado: false, ...fields });
        }
    });

    const finalCategories = fresh
        ? added.filter((c) => create.some((p) => norm(p.category) === norm(c)))
        : [...categories, ...(createCategories ? added : [])];

    return {
        mode,
        create,
        update: update.filter((u) => Object.keys(u.changes).length),
        unchanged: update.filter((u) => !Object.keys(u.changes).length).length,
        remove: fresh ? products : [],
        categories: finalCategories,
        addedCategories: fresh ? finalCategories : (createCategories ? added : []),
        skipped: rows.filter((r) => r.skipped).length + rows.filter((r) => !r.skipped && r.hasErrors).length + skippedByCategory,
        skippedByCategory,
        tooManyCategories: finalCategories.length > MAX_CATEGORIES,
        total: (fresh ? 0 : products.length) + create.length
    };
}
